import { TreeManager } from './tree-core/tree-manager';
import { enumerateSubTrees, Node } from './tree-core/tree-view';

export class SimpleTreeManager<T> implements TreeManager<T> {
  private readonly nodes = new Map<string, Node<T>>();

  //#region TreeView

  readonly roots: string[] = [];

  tryGetNode(key: string): Readonly<Node<T>> | undefined {
    return this.nodes.get(key);
  }

  getNode(key: string) {
    const node = this.tryGetNode(key);
    if (!node) {
      throw new Error(`Node ${key} not found`);
    }

    return node;
  }

  //#endregion

  //#region TreeManager

  addNode(key: string, data: T, parent: string | undefined, index: number) {
    if (this.nodes.has(key)) {
      throw new Error(`Node ${key} already exists`);
    }

    const node: Node<T> = {
      key,
      data,
      parent,
      index,
      children: [],
    };

    this.nodes.set(key, node);
    this.attachNode(node, parent, index);
  }

  updateNode(key: string, data: T) {
    this.getMutableNode(key).data = data;
  }

  moveNode(key: string, parent: string | undefined, index: number | undefined) {
    const node = this.getMutableNode(key);
    this.detachNode(node);
    this.attachNode(node, parent, index);
  }

  removeNode(key: string) {
    this.detachNode(this.getMutableNode(key));

    // remove the subtree
    for (const node of enumerateSubTrees(this, [key])) {
      this.nodes.delete(node.key);
    }
  }

  //#endregion

  clear() {
    this.nodes.clear();
    this.roots.length = 0;
  }

  private attachNode(node: Node<T>, parent: string | undefined, index: number | undefined) {
    const siblings = this.getChildrenKeys(parent);
    index ??= siblings.length;

    node.parent = parent;
    node.index = index;

    if (siblings.length < index) {
      throw new Error(`Missing children: got ${siblings.length} sibilings and a node in index ${index}`);
    } else if (siblings.length === index) {
      siblings.push(node.key);
    } else {
      for (let i = index; i < siblings.length; i++) {
        this.getMutableNode(siblings[i]).index++;
      }
      siblings.splice(index, 0, node.key);
    }
  }

  private detachNode(node: Node<T>) {
    const siblings = this.getChildrenKeys(node?.parent);

    if (siblings[node.index] !== node.key) {
      throw new Error(`Inconsistent state: node ${node.key} should be at index ${node.index} but it is not`);
    }

    siblings.splice(node.index, 1);
    for (let i = node.index; i < siblings.length; i++) {
      this.getMutableNode(siblings[i]).index--;
    }
  }

  private getChildrenKeys(key: string | undefined) {
    return key === undefined ? this.roots : this.getNode(key).children;
  }

  private getMutableNode(key: string): Node<T> {
    return this.getNode(key);
  }
}
